import { useState, type FormEvent } from 'react';
import type { JournalEntry, CreateJournalEntryPayload, UpdateJournalEntryPayload } from '../../services/journalService';
import { RichTextEditor } from '../atoms/RichTextEditor/RichTextEditor';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { Text } from '../atoms/Typography/Text';
import { FieldError } from '../atoms/FieldError/FieldError';
import { JournalEntryPhotos } from './JournalEntryPhotos';
import { JournalEntryMeasurements } from './JournalEntryMeasurements';
import { useTranslation } from '../../i18n/useTranslation';
import { JOURNAL_TAGS, journalTagLabel } from './journalTags';
import './JournalEntryForm.css';

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, '').trim();
}

export interface JournalEntryFormProps {
  aquariumId: string;
  entry?: JournalEntry;
  onSubmit: (payload: CreateJournalEntryPayload | UpdateJournalEntryPayload) => Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function JournalEntryForm({
  aquariumId,
  entry,
  onSubmit,
  onCancel,
  isSubmitting = false,
}: JournalEntryFormProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(entry?.title ?? '');
  const [content, setContent] = useState(entry?.content ?? '');
  const [visibility, setVisibility] = useState<'public' | 'private'>(
    entry?.visibility ?? 'private',
  );
  const [selectedTags, setSelectedTags] = useState<string[]>(entry?.tags ?? []);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEditing = entry !== undefined;

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (title.trim().length === 0) {
      // eslint-disable-next-line security/detect-object-injection
      newErrors['title'] = t('journal.form.titleRequired');
    } else if (title.trim().length > 500) {
      // eslint-disable-next-line security/detect-object-injection
      newErrors['title'] = t('journal.form.titleTooLong');
    }
    if (stripHtml(content).length === 0) {
      // eslint-disable-next-line security/detect-object-injection
      newErrors['content'] = t('journal.form.contentRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!validate()) return;

    const payload: CreateJournalEntryPayload | UpdateJournalEntryPayload = {
      title: title.trim(),
      content: content.trim(),
      visibility,
      tags: selectedTags,
    };

    await onSubmit(payload);
  }

  function toggleTag(tag: string): void {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag],
    );
  }

  // eslint-disable-next-line security/detect-object-injection
  const titleError = errors['title'];
  // eslint-disable-next-line security/detect-object-injection
  const contentError = errors['content'];

  return (
    <form
      className="journal-entry-form"
      onSubmit={handleSubmit}
      noValidate
      aria-label={isEditing ? t('journal.form.editAriaLabel') : t('journal.form.createAriaLabel')}
    >
      {entry && (
        // Measurements sit at the top of the entry (above text and photos). They are
        // read-only here: adding one opens a dialog with its own <form>, which must not
        // be nested in this one — measurements are added on the entry page.
        <JournalEntryMeasurements aquariumId={aquariumId} entryId={entry.id} readOnly />
      )}

      <div className="journal-entry-form__field">
        <Input
          id="journal-title"
          label={t('journal.form.titleLabel')}
          required
          inputClassName={`journal-entry-form__input${titleError ? ' journal-entry-form__input--error' : ''}`}
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder={t('journal.form.titlePlaceholder')}
          maxLength={500}
          aria-invalid={Boolean(titleError)}
          aria-describedby={titleError ? 'journal-title-error' : undefined}
          disabled={isSubmitting}
          error={titleError}
        />
      </div>

      <div className="journal-entry-form__field">
        <span className="journal-entry-form__label" id="journal-content-label">
          {t('journal.form.contentLabel')} <span className="journal-entry-form__required" aria-hidden="true">*</span>
        </span>
        <RichTextEditor
          value={content}
          onChange={setContent}
          placeholder={t('journal.form.contentPlaceholder')}
          disabled={isSubmitting}
          hasError={Boolean(contentError)}
          aria-labelledby="journal-content-label"
          aria-describedby={contentError ? 'journal-content-error' : undefined}
        />
        {contentError && (
          <FieldError id="journal-content-error">{contentError}</FieldError>
        )}
      </div>

      <div className="journal-entry-form__field">
        <span className="journal-entry-form__label">{t('journal.form.visibilityLabel')}</span>
        <div className="journal-entry-form__visibility-toggle" role="group" aria-label={t('journal.form.visibilityLabel')} aria-describedby="journal-visibility-hint">
          <Button
            type="button"
            variant="ghost"
            className={`journal-entry-form__visibility-btn${visibility === 'private' ? ' journal-entry-form__visibility-btn--active' : ''}`}
            onClick={() => setVisibility('private')}
            aria-pressed={visibility === 'private'}
            disabled={isSubmitting}
          >
            <span className="journal-entry-form__visibility-icon" aria-hidden="true">🔒</span>
            {t('journal.form.visibilityPrivate')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className={`journal-entry-form__visibility-btn${visibility === 'public' ? ' journal-entry-form__visibility-btn--active' : ''}`}
            onClick={() => setVisibility('public')}
            aria-pressed={visibility === 'public'}
            disabled={isSubmitting}
          >
            <span className="journal-entry-form__visibility-icon" aria-hidden="true">🌍</span>
            {t('journal.form.visibilityPublic')}
          </Button>
        </div>
        <Text className="journal-entry-form__visibility-hint" id="journal-visibility-hint">
          {visibility === 'private'
            ? t('journal.form.visibilityPrivateHint')
            : t('journal.form.visibilityPublicHint')}
        </Text>
      </div>

      <div className="journal-entry-form__field">
        <span className="journal-entry-form__label">{t('journal.form.tagsLabel')}</span>
        <div className="journal-entry-form__tags" role="group" aria-label={t('journal.form.tagsLabel')}>
          {JOURNAL_TAGS.map(tag => (
            <Button
              key={tag}
              type="button"
              variant="ghost"
              className={`journal-entry-form__tag${selectedTags.includes(tag) ? ' journal-entry-form__tag--selected' : ''}`}
              onClick={() => toggleTag(tag)}
              aria-pressed={selectedTags.includes(tag)}
              disabled={isSubmitting}
            >
              {journalTagLabel(tag, t)}
            </Button>
          ))}
        </div>
      </div>

      <div className="journal-entry-form__field" role="group" aria-labelledby="journal-photos-label">
        <span className="journal-entry-form__label" id="journal-photos-label">
          {t('journal.attachments.title')}
        </span>
        {entry ? (
          <JournalEntryPhotos aquariumId={aquariumId} entryId={entry.id} />
        ) : (
          <Text className="journal-entry-form__visibility-hint">
            {t('journal.attachments.saveFirst')}
          </Text>
        )}
      </div>

      <div className="journal-entry-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t('journal.form.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('journal.form.saving') : isEditing ? t('journal.form.saveChanges') : t('journal.form.createEntry')}
        </Button>
      </div>
    </form>
  );
}
